import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import type { components } from '@/api/schema'
import { api, call } from '@/api/client'
import type { ApiResult } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { CODE_LENGTH, OtpInput } from '@/ui/OtpInput'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/**
 * `/verify-mobile` — S-D2 screens 05 and 06.
 *
 * Reached straight from a mobile signup, which leaves the browser **signed in**
 * with an unverified number: `POST /auth/signup/mobile` sets the session cookie,
 * so this screen is behind a session even though it is in the public band of the
 * route table. It has to be reachable without the tenant guard — a brand-new
 * account has no organisation — and it has to survive a reload, which is why the
 * number comes from `/auth/me` rather than from navigation state.
 *
 * **Two failure screens, not one.** `OTP_INVALID` re-enables the boxes and keeps
 * what was typed, because the likeliest cause is one wrong digit. `OTP_EXPIRED`
 * clears them and offers only *Send a new code*, because retrying a dead code
 * cannot work and five wrong guesses spend it (`app/security/otp.py`). The API
 * separates the two codes for exactly this reason.
 */

/** Mirrors `CODE_TTL_SECONDS` in `app/security/otp.py` (10 * 60). */
const CODE_LIFETIME_MINUTES = 10

export function VerifyMobile(): JSX.Element {
  const navigate = useNavigate()
  const { user, status, refresh } = useAuth()

  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [changing, setChanging] = useState(false)
  const [newMobile, setNewMobile] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  /**
   * The number a code has just been texted to, when that is **not** the number
   * on the account.
   *
   * `POST /auth/mobile/change` texts the new number and changes nothing else —
   * `users.mobile` moves only when the code is typed back. So after a change
   * `user.mobile` is the *old* number, and a screen that keeps reading it tells
   * somebody holding a code on their new handset that it went to their old one.
   * `refresh()` cannot fix that: the server is right, the field is simply not
   * the answer to "where did the code go" any more.
   */
  const [pendingMobile, setPendingMobile] = useState<string | null>(null)

  /** Where the outstanding code actually went. */
  const destination = pendingMobile ?? user?.mobile ?? null

  const verify = useMutation((body: { code: string }) =>
    call(() => api.POST('/api/v1/auth/mobile/verify', { body })),
  )
  const resend = useMutation(() => call(() => api.POST('/api/v1/auth/mobile/resend', {})))
  const change = useMutation((body: { mobile: string; password?: string }) =>
    call(() => api.POST('/api/v1/auth/mobile/change', { body })),
  )

  // AUTH-9. The code that brought us here was sent by the signup, so its
  // cooldown is already running and *Send a new code* is already refused. Asked
  // for on mount rather than assumed, because the only client-side alternative
  // is to mirror the server's 45 seconds and drift from it — and the only
  // server-side one before this route existed was to press resend and read the
  // 429, which texts somebody a second code to find out whether it was allowed.
  //
  // Held until the session has resolved. `status` starts at `loading`, and an
  // authenticated route asked before `/auth/me` has answered is both a wasted
  // request and a 401 — which `call()` broadcasts as *this browser's session is
  // gone* to every listener, including the one that raises the expiry dialog.
  const outstanding = useQuery(
    (signal) =>
      status === 'authenticated'
        ? call(() => api.GET('/api/v1/auth/mobile/code', { signal }))
        : Promise.resolve<ApiResult<OutstandingCode>>({ ok: true, data: NO_CODE }),
    [status],
  )

  const expired = verify.state.failure?.code === 'OTP_EXPIRED'
  const wrong = verify.state.failure?.code === 'OTP_INVALID'

  // An expired code cannot be retried, so the boxes are cleared rather than left
  // holding digits whose only possible outcome is another refusal.
  useEffect(() => {
    if (expired) setCode('')
  }, [expired])

  // The "Resend in 0:42" S-D2 drew. Driven by the server's own `Retry-After`,
  // never by a client-side guess at the policy.
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => {
      setCooldown((seconds) => seconds - 1)
    }, 1000)
    return () => {
      window.clearTimeout(timer)
    }
  }, [cooldown])

  // Seeded from the answer, and only upward: a resend that happened while this
  // was in flight has already set a fresher figure, and overwriting it with the
  // older one would re-enable a button the server is still refusing.
  const seeded = outstanding.state.data?.resend_available_in_seconds ?? 0
  useEffect(() => {
    if (seeded > 0) setCooldown((current) => Math.max(current, seeded))
  }, [seeded])

  async function onSubmit(event?: SyntheticEvent): Promise<void> {
    event?.preventDefault()
    if (code.length !== CODE_LENGTH) return
    const result = await verify.run({ code })
    if (result.ok) {
      await refresh()
      void navigate(DEFAULT_HOME, { replace: true })
    }
  }

  async function onResend(): Promise<void> {
    setCode('')
    verify.reset()
    const result = await resend.run(undefined)
    setCooldown(
      result.ok
        ? result.data.resend_available_in_seconds
        : (result.failure.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS),
    )
  }

  /**
   * AUTH-8. Screen 05's "Change number" is one tap back to correcting the
   * number, and it has to work for the account looking at this screen — which
   * has a session, no organisation and no tenant. The footer used to link to
   * `/settings/organisation`, which sits behind `RequireTenant`, so the one
   * person who needs it is the one person the guard turns away.
   *
   * `POST /auth/mobile/change` is the endpoint that already does this. It asks
   * for the current password when the account has one, because moving the
   * number is how somebody keeps an account they have borrowed a browser for —
   * that check is the server's (`_require_current_password`) and the field
   * below is simply how it is collected.
   */
  async function onChangeNumber(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await change.run(
      user?.has_password
        ? { mobile: newMobile.trim(), password: currentPassword }
        : { mobile: newMobile.trim() },
    )
    if (!result.ok) return
    // The new number's own code is now the outstanding one, so everything on
    // this screen is about it: the boxes are cleared, the old refusal is
    // dropped, and the cooldown restarts from what the send just reported.
    setChanging(false)
    setPendingMobile(newMobile.trim())
    setNewMobile('')
    setCurrentPassword('')
    setCode('')
    verify.reset()
    setCooldown(result.data.resend_available_in_seconds)
    await refresh()
  }

  if (status === 'anonymous') {
    return (
      <PublicPage title="Sign in to confirm your number">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            Confirming a mobile number needs the account it belongs to. Sign in and we will bring
            you back here.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (changing) {
    return (
      <PublicPage
        title="Change your number"
        footer={
          <>
            Got it after all?{' '}
            <button
              type="button"
              className="app-link-button"
              onClick={() => {
                setChanging(false)
                change.reset()
              }}
            >
              Back to the code
            </button>
          </>
        }
      >
        {/* `method="post"` so a submit with dead JavaScript cannot put this
            password in the query string. Reasoning in full on `Login.tsx`. */}
        <form
          method="post"
          onSubmit={(event) => {
            void onChangeNumber(event)
          }}
        >
          <div className="ar-card__body ar-stack">
            <p className="ar-muted" style={{ margin: 0 }}>
              We will text a new code to the number you give here. The old one stops working.
            </p>
            <InlineError failure={change.state.failure} takesFocus />
            <TextField
              label="Mobile number"
              type="tel"
              required
              autoFocus
              autoComplete="tel"
              inputMode="tel"
              placeholder="+91 98490 12345"
              help="Include the country code."
              value={newMobile}
              onChange={(event) => {
                setNewMobile(event.target.value)
                change.reset()
              }}
            />
            {/* Asked for only when there is one to ask for. A Google-only
                identity has never set a password and the server skips the check
                for it (`_require_current_password`), so a required box it could
                never fill would be a dead end rather than a safeguard. */}
            {user?.has_password ? (
              <TextField
                label="Current password"
                type="password"
                required
                autoComplete="current-password"
                help="Confirms it is you moving the account to a different number."
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value)
                  change.reset()
                }}
              />
            ) : null}

            {/* AUTH-34. This screen is not one of S-D2's twelve — it is the
                in-place form AUTH-8 needed — so it takes the frame the eleven
                that *are* drawn share, rather than being the one auth screen
                with a bordered footer rail across it. */}
            <Button
              tone="primary"
              size="lg"
              block
              type="submit"
              busy={change.pending}
              busyLabel="Sending a new code"
            >
              Send a code to this number
            </Button>
          </div>
        </form>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Enter the code"
      /*
        AUTH-47. The number, and the way to correct it, belong in
        `.sc-auth__head p` above the card — not in the card body at full size
        with the change link stranded in the page footer three blocks below it.

        Screen 05 puts both in one sentence for a reason its own rationale
        gives: "the number is shown so a mistyped digit is caught before the
        code is wasted, and change number is one tap, not a restart." Reading
        the number and acting on it is one decision, so it is one line.

        AUTH-8's behaviour is unchanged — the control is still a `<button>` that
        opens the in-place form, because `/settings/organisation` is behind
        `RequireTenant` and this screen's whole audience is tenant-less.
      */
      subtitle={
        <>
          We sent a {CODE_LENGTH}-digit code to <strong>{destination ?? 'your mobile'}</strong>.{' '}
          <button
            type="button"
            className="app-link-button"
            onClick={() => {
              setChanging(true)
            }}
          >
            Change number
          </button>
        </>
      }
    >
      {/* `method="post"` so a submit with dead JavaScript cannot put this OTP in the
          query string. It is single-use and short-lived, which shortens the window
          without closing it. Reasoning in full on `Login.tsx`'s form. */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          {pendingMobile ? (
            // Only while the two differ. `resend_code` sends to the outstanding
            // row's destination, so *Send a new code* goes to the new number
            // too — it is the account that has not moved, and saying so is what
            // stops "confirm the number above" reading as a contradiction.
            <p className="ar-muted" style={{ margin: 0 }}>
              The account still uses <strong>{user?.mobile ?? 'its old number'}</strong> and keeps
              it until this code is confirmed.
            </p>
          ) : null}

          {expired ? (
            <div className="ar-alert ar-alert--warning" role="alert">
              {/* AUTH-36. `⚠` on a warning, `✕` on a refusal — the design's two
                  glyphs for two different things (`auth.html:252` against
                  `:268`). The app had `!` on both, which made an expiry that
                  nobody did anything wrong to look like a rejection. */}
              <span className="ar-alert__icon" aria-hidden="true">
                ⚠
              </span>
              <div className="ar-alert__body">
                <strong>This code has expired</strong>
                Ask for a new one — the old code will not work even if you type it correctly.
              </div>
            </div>
          ) : wrong ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                ✕
              </span>
              <div className="ar-alert__body">
                <strong>That code is not right</strong>
                {/* AUTH-50. The expiry is stated here, where it is the likely
                    explanation for the refusal, rather than in the screen's
                    opening line where it was one more thing to read before
                    typing. Screen 06's copy exactly. */}
                Check the last message — codes are {CODE_LENGTH} digits and expire after{' '}
                {CODE_LIFETIME_MINUTES} minutes.
              </div>
            </div>
          ) : (
            <InlineError failure={verify.state.failure} />
          )}
          <InlineError failure={resend.state.failure} />

          <OtpInput
            value={code}
            onChange={(next) => {
              setCode(next)
              verify.reset()
            }}
            onComplete={() => {
              void onSubmit()
            }}
            invalid={wrong}
            disabled={verify.pending}
          />

          {/* AUTH-34 / AUTH-49. Both actions are in the card body's stack. They
              were in an `.ar-card__footer` overridden inline to stack rather
              than sit in a row — an override that says the footer was the wrong
              container, since a footer's whole shape is a row of actions under
              a rule. Screens 05 and 06 draw neither the rule nor the row. */}
          {expired ? null : (
            <Button
              tone="primary"
              size="lg"
              block
              type="submit"
              busy={verify.pending}
              busyLabel="Checking the code"
              disabled={code.length !== CODE_LENGTH}
            >
              {/* AUTH-10: screen 06 relabels the primary action once a code has
                  been rejected. "Verify" describes a first attempt; after a
                  refusal the thing being offered is another go at it. */}
              {wrong ? 'Try again' : 'Verify'}
            </Button>
          )}

          {/*
            AUTH-48. Two shapes for two states, which is what the design draws
            and is also the more honest of the two.

            While the cooldown runs, screen 05 has a `.sc-auth__foot` line —
            "Didn't get it? Resend in 0:42", the seconds in `.ar-mono` so the
            digits do not shuffle the sentence as they tick. The app had a
            disabled full-width ghost button counting down inside its own label,
            which offers the reader a control and then refuses it for
            three-quarters of a minute.

            Once it is available, screen 06 has exactly that button. So the
            countdown is a sentence, and the action is a button, and neither
            pretends to be the other.
          */}
          {cooldown > 0 ? (
            <p className="app-public__cardfoot">
              Didn&rsquo;t get it? Resend in{' '}
              <span className="ar-mono">{formatCooldown(cooldown)}</span>
            </p>
          ) : (
            <Button
              tone="ghost"
              block
              busy={resend.pending}
              busyLabel="Sending a new code"
              onClick={() => {
                void onResend()
              }}
            >
              Send a new code
            </Button>
          )}
        </div>
      </form>
    </PublicPage>
  )
}

/** The shape `GET /auth/mobile/code` answers with, from the generated schema. */
type OutstandingCode = components['schemas']['OutstandingCodeResponse']

/**
 * What is true before the session has resolved, and after a code is spent.
 *
 * A literal rather than a request, so the screen has an answer to render while
 * `/auth/me` is still in flight — the alternative is an authenticated call that
 * 401s and is broadcast as an expired session.
 */
const NO_CODE: OutstandingCode = {
  code_outstanding: false,
  expires_in_seconds: 0,
  resend_available_in_seconds: 0,
}

/** Mirrors the resend cooldown in `app/security/otp.py`. A floor, not the authority. */
const RESEND_COOLDOWN_SECONDS = 45

function formatCooldown(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  return `${String(minutes)}:${String(seconds % 60).padStart(2, '0')}`
}
